import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Play, Award, CheckCircle2, Clock, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import { heroData, statsData, trustedBrands, videoTestimonials, whyChooseUs, featuredProjects, processSteps, cities, projects } from '../data';
import { ProjectModal } from '../components/ProjectModal';
import SEO from '../components/SEO';
import { getThumbnail } from '../lib/imageOptimization';

const homeIconMap = {
  Award: Award,
  CheckCircle2: CheckCircle2,
  Clock: Clock,
  Users: Users
};

const Home = ({ onQuotationClick }) => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  const handleImgError = (e) => {
    const src = e.target.src;
    if (src.endsWith('.jpg')) {
      e.target.src = src.replace('.jpg', '.png');
    }
  };

  useEffect(() => {
    if (!hasShownPopup) {
      const timer = setTimeout(() => {
        onQuotationClick();
        setHasShownPopup(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [onQuotationClick, hasShownPopup]);

  useEffect(() => {
    if (videoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [videoModalOpen]);

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setVideoModalOpen(true);
  };

  const [selectedProject, setSelectedProject] = useState(null);

  const findProjectByTitle = (featuredTitle) => {
    const stopWords = ['villa', 'residence', 'rental', 'property', 'commercial'];
    const words = featuredTitle.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ').filter(Boolean);
    return projects.find(p => {
      const title = p.title.toLowerCase();
      return words.filter(w => w.length > 2 && !stopWords.includes(w)).every(w => title.includes(w));
    });
  };

  const openProject = (featured) => {
    const full = findProjectByTitle(featured.title);
    if (full) setSelectedProject(full);
  };

  return (
    <>
      <div>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden -mt-16 sm:-mt-20">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://res.cloudinary.com/dpt7mjxaj/video/upload/v1780150151/AASHI_CONSTRUCTION_wbcv6p.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="hero-slide-in-left" style={{ animationDelay: '0.2s' }}>
                {/* <div className="inline-block px-4 py-2 bg-brand-600/20 backdrop-blur-sm rounded-full text-sm font-semibold text-white/90 mb-6">
                  {heroData.ctaText}
                </div> */}
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight hero-fade-in" style={{ animationDelay: '0.4s' }}>
                {heroData.headline}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-10 max-w-2xl leading-relaxed hero-fade-in" style={{ animationDelay: '0.6s' }}>
                {heroData.subheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 hero-fade-in-up" style={{ animationDelay: '0.8s' }}>
                <Button
                  onClick={onQuotationClick}
                  size="lg"
                  className="bg-brand-600 hover:bg-brand-700 text-white px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-medium shadow-2xl hover:shadow-brand-600/40 transition-all duration-300 hover:scale-105"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg transition-all duration-300"
                  onClick={() => window.location.href = '/projects'}
                >
                  View Our Work
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/40 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="text-4xl md:text-5xl font-bold text-brand-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Brands Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-center mb-3 md:mb-4 text-gray-900">
            Trusted Brands
          </h2>
          <p className="text-center text-gray-600 text-sm md:text-base mb-8 md:mb-12 max-w-2xl mx-auto px-4">
            We use premium materials from India's most trusted brands
          </p>
          
          <BrandsCarousel brands={trustedBrands} />
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900">
              Client Stories
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Hear from our distinguished clients about their journey to their dream homes
            </p>
          </div>

          <TestimonialCarousel
            videos={videoTestimonials}
            onSelect={openVideoModal}
          />
        </div>
      </section>

      {/* Video Modal */}
      {videoModalOpen && selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300"
          onClick={() => setVideoModalOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoModalOpen(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors duration-200"
            >
              ×
            </button>
            <iframe
              src={selectedVideo.videoUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900">
              Why Choose Us
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Ensures a blend of creative innovation, technical precision, and client-focused collaboration to turn visions into reality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = homeIconMap[item.icon];
              return (
                <div
                  key={index}
                  className="text-center p-4 sm:p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
                >
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-brand-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Preview */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900">
              Featured Projects
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our portfolio of quality homes built across Bangalore
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer"
                onClick={() => openProject(project)}
              >
                <img
                  src={getThumbnail(project.image, 600)}
                  alt={project.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-64 md:h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                  onError={handleImgError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-white/90">{project.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => window.location.href = '/projects'}
              size="lg"
              className="bg-brand-600 hover:bg-brand-700 text-white px-8 sm:px-10 py-4 sm:py-6 text-sm sm:text-base"
            >
              View All Projects
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900">
              Our Seamless Process
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              From vision to reality in four expertly managed phases
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-11">
            {processSteps.map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-brand-600 to-transparent -ml-24" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900">
              Building Dreams Across India
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Serving premium locations in major metropolitan cities
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {cities.map((city, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-center border border-gray-100"
              >
                <p className="text-lg font-bold text-gray-900">{city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to Build Your Dream Home?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Let's discuss your project and provide a free, no-obligation quote
          </p>
          <Button
            onClick={onQuotationClick}
            size="lg"
            className="bg-brand-600 hover:bg-brand-700 text-white px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg font-medium shadow-2xl hover:shadow-brand-600/50 transition-all duration-300 hover:scale-105"
          >
            Get Free Quote
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
      {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />}
    </>
  );
};

const BrandsCarousel = ({ brands }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    dragFree: true,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const timer = setInterval(() => emblaApi.scrollNext(), 2000);
    return () => clearInterval(timer);
  }, [emblaApi]);

  return (
    <div className="overflow-hidden" ref={emblaRef}>
      <div className="flex gap-4 md:gap-8">
        {[...brands, ...brands, ...brands].map((brand, index) => (
          <div
            key={index}
            className="flex-shrink-0 w-32 md:w-40 h-20 md:h-24 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-600 transition-colors duration-300"
          >
            <img src={brand.logo} alt={brand.name} loading="lazy" className="w-full h-full object-contain p-3" />
          </div>
        ))}
      </div>
    </div>
  );
};

const TestimonialCarousel = ({ videos, onSelect: onVideoSelect }) => {
  const allVideos = [...videos, ...videos, ...videos];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelectCb = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap() % videos.length);
  }, [emblaApi, videos.length]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelectCb);
    onSelectCb();
    const timer = setInterval(() => emblaApi.scrollNext(), 3500);
    return () => {
      emblaApi.off('select', onSelectCb);
      clearInterval(timer);
    };
  }, [emblaApi, onSelectCb]);

  const goTo = (i) => {
    const count = emblaApi?.scrollSnapList().length;
    if (!count) return;
    emblaApi?.scrollTo(Math.floor(count / 3) * videos.length + i);
  };

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {allVideos.map((video, idx) => (
            <div key={idx} className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.33%] pl-4">
              <div
                className="group cursor-pointer"
                onClick={() => onVideoSelect(video)}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105">
                  <img
                    src={video.thumbnail}
                    alt={`Client video ${video.id}`}
                    loading="lazy"
                    className="w-full h-48 sm:h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Play className="w-8 h-8 text-brand-600 ml-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
      >
        <ChevronLeft className="w-5 h-5 text-gray-700" />
      </button>
      <button
        onClick={() => emblaApi?.scrollNext()}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
      >
        <ChevronRight className="w-5 h-5 text-gray-700" />
      </button>

      <div className="flex justify-center gap-2 mt-6">
        {videos.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              i === selectedIndex
                ? 'bg-brand-600 w-6'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default Home;
